import React, { useEffect, useMemo, useRef, useState } from 'react';
import { actions } from '@ecosystem/newron-design-system';
import { DataContentProps, useUiAction } from '../../newron-core';
import { Instructions } from '../../content/instructions';
import { Loading } from '../../design/loading/Loading';
import { makeHeatmapData } from './data';
import { HeatmapInfo, HeatmapTable } from '../../design/charts/heatmap';
import { DataType, Entity, UiAction } from '../../types';
import { getExportContainer } from '../../export';
import { getSlicedHeatmapInfo } from './heatmap.utils';
import { useCorePageState } from '../../page/provider';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { createChartRef } from '../utils';
import classNames from 'classnames';
import { useMobile } from '../../context/mobile/mobile.provider';
import { useMobileChart } from '../mobileChart/mobileChart.hook';
import { getExportHeadingTitle } from '../../export/utils/pptUtils';

const { Button } = actions;

function Heatmap({
  data,
  isLoading,
  componentSettings,
  updateSettings,
  instructions = {},
  componentName,
  collection,
  componentHeadingTitle,
}: DataContentProps) {
  const [dataStamp, setDataStamp] = useState<number>();
  const [info, setInfo] = useState<HeatmapInfo | undefined>();
  const [limitRows, setLimitRows] = useState<number>(8);
  const [exportRefLength, setExportRefLength] = useState<number>(0);

  const { dsl, globalWarnings } = useCorePageState();
  const exportRef = useRef<HTMLDivElement[]>([]);

  const allColumns = data?.getAllColumns() ?? [];
  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];
  const { isMobile } = useMobile();
  const { chartFullscreen, setChartFullscreen, MobileChartButton } = useMobileChart();
  const heatMapExportHeadingTitle = getExportHeadingTitle(componentHeadingTitle, sharedBrands);
  useOnCollectionExportReady({
    collection,
    dsl,
    chartSliceRefs: exportRef,
    componentName,
    componentSettings,
    allColumns,
    componentHeadingTitle: heatMapExportHeadingTitle,
    refLength: exportRefLength,
  });

  const { perform } = useUiAction();

  const myInstructions = new Instructions(instructions, componentSettings);

  const xGrouping = myInstructions.getString('xGrouping');
  const sortX = myInstructions.getBoolean('sortX', false);
  const yGrouping = myInstructions.getString('yGrouping');
  const sortY = myInstructions.getBoolean('sortY', false);
  const kpi = myInstructions.getString('kpi');
  const clickAction = myInstructions.getObject('clickAction') as UiAction;

  const updateData = () => {
    if (!data) {
      return;
    }

    if (!data.getDataColumns().find(col => col.name === kpi)) {
      const defaultColumn = data
        .getDataColumns()
        .find(
          col => (col.dataType === DataType.Decimal || col.dataType === DataType.Integer) && col.arrayLength !== null
        );
      if (!defaultColumn) {
        console.warn(`column ${kpi} is not valid for heatmap and no alternative could be found`);
        return;
      }
      const kpiParam = myInstructions.getSettingsName('kpi');
      if (!kpiParam) {
        console.warn(
          `column ${kpi} is not valid for heatmap, trying to change to ${defaultColumn.name} but kpi is not set via settings`
        );
        return;
      }
      updateSettings({
        [kpiParam]: [defaultColumn.name],
      });
      return;
    }

    const info = makeHeatmapData(data, {
      xGrouping,
      yGrouping,
      sortX,
      sortY,
      kpi,
    });
    setInfo(info);
    setDataStamp(data.stamp);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || dataStamp === data.stamp) {
      return;
    }
    updateData();
    setLimitRows(8);
  }, [data, dataStamp]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setDataStamp(0);
  }, [kpi]);

  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  const handleClick = (xValue: Entity, yValue: Entity) => {
    if (!clickAction) {
      return;
    }

    perform(clickAction, {
      xGrouping: [xGrouping],
      yGrouping: [yGrouping],
      xValue: [xValue.value],
      xLabel: [xValue.label],
      yValue: [yValue.value],
      yLabel: [yValue.label],
      kpi: [kpi],
    });
  };

  const ExportContainer = useMemo(() => getExportContainer(componentName), []);

  if (collection && data && !data.hasDataResultSetsValues() && !data.hasDataInSomeGroupedSets()) {
    collection.onExportReady(null);
    return <EmptyState />;
  }

  return (
    <div className={classNames({ 'chart-full-screen': chartFullscreen })}>
      {isMobile && !isLoading && (
        <MobileChartButton chartFullscreen={chartFullscreen} setChartFullscreen={setChartFullscreen} />
      )}
      <div
        className={classNames('heatmap relative w-full max-w-full bg-white rounded-2', {
          'min-h-56': isLoading,
          'pt-7-important': isMobile,
        })}
      >
        <Loading isActive={isLoading} withIndicator />
        {info && <HeatmapTable info={info} limitRows={limitRows} onCellClick={clickAction ? handleClick : undefined} />}
        {info && info.rows.length > limitRows && (
          <Button mode="simple" onClick={() => setLimitRows(10000)}>
            Show {info.rows.length - limitRows} more
          </Button>
        )}
        <ExportContainer>
          {info &&
            getSlicedHeatmapInfo(info).map((table, idx, arr) => (
              <div
                className={`${componentName}-export-slide`}
                key={`heatmap-table-${idx}`}
                ref={createChartRef(idx, arr.length, setExportRefLength, exportRef)}
              >
                {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
                <HeatmapTable info={table} onCellClick={clickAction ? handleClick : undefined} />
              </div>
            ))}
        </ExportContainer>
      </div>
    </div>
  );
}

export { Heatmap };
